import { useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { motion, useReducedMotion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

type Localized = { en: string; tr: string }

const ui = {
  eyebrow: { en: 'Free download', tr: 'Ücretsiz indirin' },
  name: { en: 'Name', tr: 'Ad Soyad' },
  email: { en: 'Work e-mail', tr: 'İş e-postası' },
  required: { en: '(required)', tr: '(zorunlu)' },
  namePlaceholder: { en: 'Jane Doe', tr: 'Ad Soyad' },
  submit: { en: 'Get the download →', tr: 'İndirme bağlantısını al →' },
  sending: { en: 'Sending…', tr: 'Gönderiliyor…' },
  success: {
    en: 'Ready: your download is below.',
    tr: 'Hazır: indirme bağlantınız aşağıda.',
  },
  downloadCta: { en: 'Download the PDF', tr: "PDF'i indir" },
  failure: {
    en: 'Something went wrong. You can mail <a href="mailto:sales@monofor.com" class="underline">sales@monofor.com</a> instead.',
    tr: 'Bir şeyler ters gitti. Dilerseniz <a href="mailto:sales@monofor.com" class="underline">sales@monofor.com</a> adresine yazabilirsiniz.',
  },
}

interface AssetDownloadProps {
  /** Key into config/lead-magnets.json */
  asset: string
  eyebrow?: Localized
  title: Localized
  copy: Localized
}

/**
 * Gated lead-magnet download: e-mail in, instant download link out.
 * The API tags the contact in Mailchimp (per-asset tag) for follow-up.
 */
export default function AssetDownload({
  asset,
  eyebrow,
  title,
  copy,
}: AssetDownloadProps) {
  const { locale } = useRouter()
  const reducedMotion = useReducedMotion()
  const lang = locale === 'tr' ? 'tr' : 'en'
  const nameField = useRef<HTMLInputElement>(null)
  const emailField = useRef<HTMLInputElement>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [responseType, setResponseType] = useState<'success' | 'failure' | null>(null)
  const [responseMessage, setResponseMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setResponseType(null)
    setResponseMessage('')
    try {
      const res = await fetch('/api/subscribe_pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: nameField.current?.value,
          email: emailField.current?.value,
          asset,
        }),
      })
      const data = await res.json()
      if (res.ok && data.downloadUrl) {
        setDownloadUrl(data.downloadUrl)
        setResponseType('success')
        setResponseMessage(ui.success[lang])
      } else {
        setResponseType('failure')
        setResponseMessage(ui.failure[lang])
      }
    } catch {
      setResponseType('failure')
      setResponseMessage(ui.failure[lang])
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Section tone="brand" density="default" id="download">
      <Container size="xl">
        <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
          <div>
            <Eyebrow>{(eyebrow ?? ui.eyebrow)[lang]}</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              {title[lang]}
            </Heading>
            <Text size="xl" tone="secondary" className="mt-6">
              {copy[lang]}
            </Text>
          </div>

          <motion.form
            initial={reducedMotion ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={handleSubmit}
            className="rounded-3xl border border-line-subtle bg-surface p-8 shadow-card lg:p-10"
          >
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="asset-name"
                  className="block text-body-sm font-medium text-ink-primary"
                >
                  {ui.name[lang]}{' '}
                  <span className="text-ink-tertiary">{ui.required[lang]}</span>
                </label>
                <input
                  id="asset-name"
                  ref={nameField}
                  type="text"
                  name="name"
                  required
                  minLength={2}
                  placeholder={ui.namePlaceholder[lang]}
                  autoComplete="name"
                  className="mt-2 w-full rounded-xl border border-line bg-surface px-4 py-3 text-body-md text-ink-primary placeholder:text-ink-muted focus:border-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              <div>
                <label
                  htmlFor="asset-email"
                  className="block text-body-sm font-medium text-ink-primary"
                >
                  {ui.email[lang]}{' '}
                  <span className="text-ink-tertiary">{ui.required[lang]}</span>
                </label>
                <input
                  id="asset-email"
                  ref={emailField}
                  type="email"
                  name="email"
                  required
                  placeholder="you@your-company.com"
                  autoComplete="email"
                  className="mt-2 w-full rounded-xl border border-line bg-surface px-4 py-3 text-body-md text-ink-primary placeholder:text-ink-muted focus:border-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              {responseMessage && (
                <div
                  className={`rounded-xl px-4 py-3 text-body-sm ${
                    responseType === 'success'
                      ? 'bg-accent-50 text-accent-800'
                      : 'bg-[#fef2f2] text-[#b91c1c]'
                  }`}
                  dangerouslySetInnerHTML={{ __html: responseMessage }}
                />
              )}
              {downloadUrl ? (
                <a
                  href={downloadUrl}
                  download
                  className="inline-flex w-full items-center justify-center rounded-pill bg-brand-900 px-7 py-3.5 text-body-md font-medium text-white transition hover:bg-brand-800"
                >
                  {ui.downloadCta[lang]} ↓
                </a>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex w-full items-center justify-center rounded-pill bg-brand-900 px-7 py-3.5 text-body-md font-medium text-white transition hover:bg-brand-800 disabled:opacity-50"
                >
                  {isSubmitting ? ui.sending[lang] : ui.submit[lang]}
                </button>
              )}
            </div>
          </motion.form>
        </div>
      </Container>
    </Section>
  )
}
